import React, { useEffect, useRef, useState } from 'react'
import { Container, Row, Col, Spinner, TimedAlert, TopBar } from 'components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/configStore'
import { helpIcon, calcIcon, logoutIcon, moneyIcon } from '../../assets'
import './profile.scss'
import { useBindDispatch, useLogout, useAnalytics } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { getConnectUser, constants } from 'helpers'
import { CSSTransition } from 'react-transition-group'
import { VeMetrics } from './VeMetrics'
import { colors } from 'styles/colors'

export const Profile = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const connectUser = getConnectUser()
    const { trackEvent } = useAnalytics()

    const {
        loading,
        userData,
        error: userDataError
    } = useSelector(({ home }: RootState) => home)
    const { veDepositData } = useSelector(
        ({ veDeposit }: RootState) => veDeposit[connectUser.username] || {}
    )
    const [animationList, setAnimationList] = useState(false)
    const [animate, setAnimate] = useState(false)
    const nodeRef = useRef(null)
    const { logout } = useLogout()
    const { homeHandler, openVillagePageHandler, filterSelectorHandler } =
        useBindDispatch()
    const { VE_PROFILE } = constants.ANALYTICS

    useEffect(() => {
        trackEvent({
            category: VE_PROFILE.VE_PROFILE,
            action: VE_PROFILE.VE_PROFILE_ACTION,
            name: VE_PROFILE.VE_PROFILE_VIEWED
        })
        setAnimate(true)
        if (!userData.name) {
            homeHandler({
                veCode: connectUser.username,
                country: connectUser.country
            })
        }
    }, [])

    const cardClickHandler = (filterOptions: string[], clientTab: number) => {
        const hasMultipleVillages = connectUser?.veVillages.length > 1

        openVillagePageHandler({
            filterOptions,
            clientTab,
            villageId: hasMultipleVillages
                ? ''
                : connectUser?.veVillages[0]?.sf_id
        })

        filterOptions.forEach((filterOption) =>
            filterSelectorHandler({ selectedFilter: filterOption })
        )

        const navigationUrl = `/village?from_tab=${clientTab}`

        navigate(navigationUrl)
    }

    const depositHandler = () =>
        navigate(`/profile/deposits?ve_name=${userData.name}`)

    const { name } = userData || { name: '' }

    const profileButtons = [
        {
            name: t('profile.calculator'),
            onClick: () => {
                trackEvent({
                    category: VE_PROFILE.VE_PROFILE,
                    action: VE_PROFILE.VE_CALCULATOR_ACTION,
                    name: VE_PROFILE.VE_CALCULATOR_VIEWED
                })
                navigate('/calcapp')
            },
            icon: calcIcon({})
        },
        {
            name: t('profile.cards'),
            onClick: () => navigate('/card-inventory', { state: { name } }),
            icon: moneyIcon({ color: '#2c70e3' })
        },
        {
            name: t('profile.help'),
            icon: helpIcon({})
        }
    ]

    const headerSection = (
        <div className="sticky-header" id="sticky-header">
            <TopBar color={colors.blue}>
                <p>{t('profile.title')}</p>
                <button
                    className="profile-logout-btn"
                    onClick={() => {
                        logout()
                    }}
                    aria-label="logout"
                >
                    {logoutIcon({})}
                </button>
            </TopBar>
        </div>
    )

    const profileInfoSection = (
        <div className="profile-user-info">
            <p className="profile-name">{name}</p>
            <div className="profile-buttons-container">
                {profileButtons.map(({ onClick, name, icon }, id) => (
                    <button
                        key={`${name}-${id}`}
                        className="profile-button"
                        onClick={onClick}
                        aria-label={name}
                    >
                        {icon}
                        <p>{name}</p>
                    </button>
                ))}
            </div>
        </div>
    )

    const renderProfile = userDataError ? (
        <TimedAlert type="floating" status="error">
            {userDataError}
        </TimedAlert>
    ) : (
        <>
            {headerSection}

            <CSSTransition
                unmountOnExit
                timeout={constants.ANIMATION_TIMEOUT}
                in={animate}
                classNames="generic"
                appear
                onEnter={() => setAnimationList(true)}
                nodeRef={nodeRef}
            >
                <div className="page-wrapper" ref={nodeRef}>
                    <Container className="profile-main">
                        <Row>
                            <Col md={12}>
                                {profileInfoSection}

                                <VeMetrics
                                    animationList={animationList}
                                    veMetrics={userData}
                                    veDeposit={veDepositData}
                                    veUser={connectUser}
                                    canViewDeposits={true}
                                    canViewDepositGap={true}
                                    cardClickHandler={cardClickHandler}
                                    depositHandler={depositHandler}
                                />
                            </Col>
                        </Row>
                    </Container>
                </div>
            </CSSTransition>
        </>
    )

    const profilePage = loading ? (
        <Spinner size={'90'} pageSpinner={true} fullscreen={true} />
    ) : (
        renderProfile
    )
    return profilePage
}
